import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router'
import { Eye, ShieldOff } from 'lucide-react'
import { adminMenuKeyForNavPath, getAdminMenu } from '@fiket/shared'
import { usePermissions } from '@/lib/permissions'

/**
 * v2 콘텐츠 가드 (FIKET-417) — 현재 경로를 ADMIN_MENUS 메뉴로 해석해
 * 보기 권한이 없으면 "접근 권한 없음" 패널, 편집 권한이 없으면 읽기 전용 배너를 얹는다.
 * 서버가 API 를 403 으로 막는 것이 본질이고 이 가드는 안내용이다.
 */
export function MenuAccessGuard({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const { isManager, canView, canEdit } = usePermissions()
  const menuKey = adminMenuKeyForNavPath(pathname)

  if (menuKey && !canView(menuKey)) {
    const label = getAdminMenu(menuKey)?.label ?? menuKey
    return (
      <div className="flex flex-col items-start gap-3 rounded-[2px] border border-[#d5dbdb] bg-white px-6 py-8">
        <div className="flex items-center gap-2 text-base font-bold text-[#16191f]">
          <ShieldOff className="size-5 text-[#c4443f]" />
          접근 권한 없음
        </div>
        <p className="text-sm text-[#5f6b7a]">
          현재 권한 그룹에는 <strong className="text-[#16191f]">{label}</strong> 메뉴 보기 권한이 없습니다. 필요하면 관리자에게 요청하세요.
        </p>
        <Link to="/v2" className="text-[0.8125rem] text-[#0972d3] hover:underline">
          홈으로
        </Link>
      </div>
    )
  }

  const readOnly = isManager && !!menuKey && !canEdit(menuKey)

  return (
    <>
      {readOnly && (
        <div
          role="status"
          className="mb-3 flex items-center gap-2 rounded-[2px] border border-[#0972d3]/30 bg-[#f1faff] px-3 py-1.5 text-xs text-[#0972d3]"
        >
          <Eye className="size-3.5" />
          읽기 전용 권한입니다. 이 메뉴의 변경 작업은 서버에서 차단됩니다.
        </div>
      )}
      {children}
    </>
  )
}
